import type { GanttTask, TimelineSummary, TimelinePhase, TimelineMilestone } from '../types/gantt';
import { gardeners, type Gardener } from '../data/gardeners';
import {
	isCompanyWorkingDay,
	nextCompanyWorkingDay,
	addCompanyWorkdays,
	countCompanyWorkdaysBetween,
	getDateForCompanyWorkdayIndex,
	resolveScheduleCalendar,
	type CompanyNonWorkingDate
} from './companySchedule';
import { toLocalDateString, toTimelineDateIso } from './dateUtils';
import { isUserOnTimeOff } from './userTimeOff';
import type { Language } from '../i18n';

/** Company calendar for smart scheduling (working days + non-working dates). */
export type CompanyCalendar = {
	workingDays: number[] | null;
	nonWorkingDates: CompanyNonWorkingDate[];
};

/** Normalize calendar so scheduling always skips weekends/holidays (defaults to Mon–Fri). */
export function normalizeCompanyCalendar(cal?: CompanyCalendar | null): CompanyCalendar {
	const resolved = resolveScheduleCalendar(cal?.workingDays, cal?.nonWorkingDates);
	return {
		workingDays: resolved.workingDays,
		nonWorkingDates: resolved.nonWorkingDates
	};
}

// Helpers to respect working days (skip weekends when no company calendar)
function isWeekend(date: Date): boolean {
	const day = date.getDay(); // 0 = Sunday, 6 = Saturday
	return day === 0 || day === 6;
}

function moveToNextWorkday(date: Date): Date {
	const result = new Date(date);
	while (isWeekend(result)) {
		result.setDate(result.getDate() + 1);
	}
	return result;
}

function addWorkdays(start: Date, workdays: number): Date {
	const result = new Date(start);
	let added = 0;
	while (added < workdays) {
		result.setDate(result.getDate() + 1);
		if (!isWeekend(result)) {
			added++;
		}
	}
	return result;
}

/** Get the calendar date for the Nth workday (1-based). Day 1 = first workday on or after startDate. */
function getDateForWorkdayIndex(startDate: Date, workdayIndex: number): Date {
	const firstWorkday = moveToNextWorkday(new Date(startDate));
	if (workdayIndex <= 1) return firstWorkday;
	return addWorkdays(firstWorkday, workdayIndex - 1);
}

/** Count working days (Mon–Fri) between two dates, inclusive. Used so duration matches the Gantt (workday-based). */
function countWorkdaysBetween(start: Date, end: Date): number {
	const startD = new Date(start);
	startD.setHours(0, 0, 0, 0);
	const endD = new Date(end);
	endD.setHours(0, 0, 0, 0);
	if (endD < startD) return 0;
	let count = 0;
	const cur = new Date(startD);
	while (cur <= endD) {
		if (!isWeekend(cur)) count++;
		cur.setDate(cur.getDate() + 1);
	}
	return count;
}

/** Workday index (1-based) from project start: first workday = 1. Used for cascade shifting. */
function getWorkdayIndex(projectStartDate: Date, date: Date): number {
	const first = moveToNextWorkday(new Date(projectStartDate));
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	if (d < first) return 0;
	return countWorkdaysBetween(first, d);
}

/** Workday index (1-based) from project start using company calendar when provided. */
function getWorkdayIndexCal(projectStartDate: Date, date: Date, cal?: CompanyCalendar | null): number {
	const first = firstWorkday(new Date(projectStartDate), cal);
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	if (d < first) return 0;
	if (cal) {
		return countCompanyWorkdaysBetween(first, d, cal.workingDays, cal.nonWorkingDates);
	}
	return countWorkdaysBetween(first, d);
}

/** Add N workdays to a date; N can be negative (moves backward). */
function addWorkdaysSigned(start: Date, workdays: number): Date {
	if (workdays >= 0) return addWorkdays(start, workdays);
	let cur = new Date(start);
	let remaining = -workdays;
	while (remaining > 0) {
		cur.setDate(cur.getDate() - 1);
		if (!isWeekend(cur)) remaining--;
	}
	return cur;
}

/** Add signed workdays using company calendar when provided. */
function addWorkdaysSignedCal(start: Date, workdays: number, cal?: CompanyCalendar | null): Date {
	if (workdays >= 0) {
		const { workingDays, nonWorkingDates } = resolveScheduleCalendar(cal?.workingDays, cal?.nonWorkingDates);
		return addCompanyWorkdays(start, workdays, workingDays, nonWorkingDates);
	}
	const { workingDays, nonWorkingDates } = resolveScheduleCalendar(cal?.workingDays, cal?.nonWorkingDates);
	let cur = new Date(start);
	let remaining = -workdays;
	while (remaining > 0) {
		cur.setDate(cur.getDate() - 1);
		if (isCompanyWorkingDay(cur, workingDays, nonWorkingDates)) remaining--;
	}
	return cur;
}

/** First workday on or after start (company calendar if provided, else Mon–Fri). */
function firstWorkday(start: Date, cal?: CompanyCalendar | null): Date {
	const { workingDays, nonWorkingDates } = resolveScheduleCalendar(cal?.workingDays, cal?.nonWorkingDates);
	return nextCompanyWorkingDay(start, workingDays, nonWorkingDates);
}

/** Add N workdays (company calendar if provided). */
function addWorkdaysCal(start: Date, n: number, cal?: CompanyCalendar | null): Date {
	const { workingDays, nonWorkingDates } = resolveScheduleCalendar(cal?.workingDays, cal?.nonWorkingDates);
	return addCompanyWorkdays(start, n, workingDays, nonWorkingDates);
}

/** Nth workday from start (1-based) (company calendar if provided). */
function getDateForWorkdayIndexCal(start: Date, workdayIndex: number, cal?: CompanyCalendar | null): Date {
	const { workingDays, nonWorkingDates } = resolveScheduleCalendar(cal?.workingDays, cal?.nonWorkingDates);
	return getDateForCompanyWorkdayIndex(start, workdayIndex, workingDays, nonWorkingDates);
}

/** Parsed step from plan implementation instructions (e.g. "Day 1: ..." or "Day 3-5: ..."). */
export type ParsedInstructionStep = {
	dayStart: number;
	dayEnd: number;
	name: string;
	description: string;
};

/** Structured timeline task shape coming from AI output. */
export type AiTimelineTask = {
	title?: string;
	description?: string;
	dayStart?: number;
	dayEnd?: number;
	dependsOnSteps?: number[];
	seasonal_ban_candidate?: boolean;
	seasonal_ban_reason?: string;
	required_skills_for_step?: string[];
	required_gardeners_for_step?: number;
};

/** Parse AI-generated implementation instructions into day ranges and descriptions. */
export function parseImplementationInstructions(instructions: string[]): ParsedInstructionStep[] {
	const steps: ParsedInstructionStep[] = [];
	// Match "Day 1:", "Days 2-4:", German "Tag 1:" / "Tage 2-4:", Croatian "Dan 1:" / "Dani 2-4:"
	const rangePattern = /^(?:Days|Tage|Dani)\s*(\d+)\s*[-–]\s*(\d+)\s*:\s*(.+)$/iu;
	const singlePattern = /^(?:Day|Tag|Dan)\s*(\d+)\s*:\s*(.+)$/iu;

	for (const line of instructions) {
		const trimmed = line.trim();
		if (!trimmed) continue;
		const rangeMatch = trimmed.match(rangePattern);
		let dayStart = 0;
		let dayEnd = 0;
		let description = '';
		if (rangeMatch) {
			dayStart = parseInt(rangeMatch[1], 10);
			dayEnd = Math.max(dayStart, parseInt(rangeMatch[2], 10));
			description = rangeMatch[3].trim();
		} else {
			const singleMatch = trimmed.match(singlePattern);
			if (!singleMatch) continue;
			dayStart = parseInt(singleMatch[1], 10);
			dayEnd = dayStart;
			description = singleMatch[2].trim();
		}
		if (!Number.isFinite(dayStart) || dayStart < 1) continue;
		// Task name: first sentence or first N chars
		const firstSentenceEnd = description.search(/[.!?]\s/);
		const nameSource = firstSentenceEnd > 0 ? description.slice(0, firstSentenceEnd + 1) : description;
		// Do not truncate task names here; truncation caused permanently shortened DB titles.
		const name = nameSource;
		steps.push({ dayStart, dayEnd, name, description });
	}
	return steps;
}

/** Build Gantt tasks from structured AI timeline tasks. */
export function ganttTasksFromStructuredTimeline(
	timelineTasks: AiTimelineTask[],
	projectStartDate: Date,
	companyCalendar?: CompanyCalendar | null
): GanttTask[] {
	if (!Array.isArray(timelineTasks) || timelineTasks.length === 0) return [];

	const startDate = firstWorkday(new Date(projectStartDate), companyCalendar);
	const normalized = timelineTasks
		.map((task, index) => {
			const dayStartRaw = Number(task?.dayStart ?? 0);
			const dayEndRaw = Number(task?.dayEnd ?? dayStartRaw);
			const dayStart = Number.isFinite(dayStartRaw) ? Math.max(1, Math.floor(dayStartRaw)) : 0;
			const dayEndCandidate = Number.isFinite(dayEndRaw) ? Math.floor(dayEndRaw) : 0;
			const dayEnd = dayStart > 0 ? Math.max(dayStart, dayEndCandidate || dayStart) : 0;
			if (dayStart === 0) return null;

			const description = String(task?.description ?? '').trim();
			const title = String(task?.title ?? '').trim();
			const fallbackName = description
				? (description.search(/[.!?]\s/) > 0 ? description.slice(0, description.search(/[.!?]\s/) + 1) : description)
				: `Task ${index + 1}`;

			const dependsOnSteps = Array.isArray(task?.dependsOnSteps)
				? task.dependsOnSteps
						.map((n) => Number(n))
						.filter((n) => Number.isFinite(n) && n > 0)
						.map((n) => Math.floor(n))
				: [];

			return {
				index,
				dayStart,
				dayEnd,
				name: title || fallbackName,
				description: description || title || fallbackName,
				dependsOnSteps,
				legalSeasonalBanCandidate: Boolean(task?.seasonal_ban_candidate ?? false),
				legalSeasonalBanReason: String(task?.seasonal_ban_reason ?? '').trim() || undefined,
				requiredSkills: Array.isArray(task?.required_skills_for_step)
					? task.required_skills_for_step.filter(Boolean).map((s) => String(s))
					: undefined,
				requiredGardeners:
					typeof task?.required_gardeners_for_step === 'number' && task.required_gardeners_for_step > 0
						? Math.floor(task.required_gardeners_for_step)
						: undefined
			};
		})
		.filter(Boolean) as Array<{
		index: number;
		dayStart: number;
		dayEnd: number;
		name: string;
		description: string;
		dependsOnSteps: number[];
		legalSeasonalBanCandidate?: boolean;
		legalSeasonalBanReason?: string;
		requiredSkills?: string[];
		requiredGardeners?: number;
	}>;

	if (normalized.length === 0) return [];

	const idByStep = new Map<number, string>();
	normalized.forEach((item, idx) => {
		idByStep.set(idx + 1, `plan-step-${idx + 1}`);
	});

	return normalized.map((step, idx) => {
		const id = idByStep.get(idx + 1) ?? `plan-step-${idx + 1}`;
		const start = getDateForWorkdayIndexCal(startDate, step.dayStart, companyCalendar);
		const end = getDateForWorkdayIndexCal(startDate, step.dayEnd, companyCalendar);
		const dependencies = step.dependsOnSteps
			.map((n) => idByStep.get(n))
			.filter((depId): depId is string => Boolean(depId));

		return {
			id,
			name: step.name,
			startDate: start.toISOString(),
			endDate: end.toISOString(),
			duration: Math.max(1, step.dayEnd - step.dayStart + 1),
			progress: 0,
			dependencies: dependencies.length > 0 ? dependencies : idx > 0 ? [`plan-step-${idx}`] : [],
			type: 'phase',
			status: 'not_started',
			description: step.description.length > 220 ? `${step.description.slice(0, 220)}...` : step.description,
			legalSeasonalBanCandidate: step.legalSeasonalBanCandidate,
			legalSeasonalBanReason: step.legalSeasonalBanReason,
			requiredSkills: step.requiredSkills,
			requiredGardeners: step.requiredGardeners
		} as GanttTask;
	});
}

/** Build Gantt tasks from plan implementation instructions. Uses workdays; Day 1 = first workday after project start. Respects company calendar when provided. */
export function ganttTasksFromPlanInstructions(
	instructions: string[],
	projectStartDate: Date,
	companyCalendar?: CompanyCalendar | null
): GanttTask[] {
	const steps = parseImplementationInstructions(instructions);
	if (steps.length === 0) return [];

	const tasks: GanttTask[] = [];
	const startDate = firstWorkday(new Date(projectStartDate), companyCalendar);

	steps.forEach((step, index) => {
		const phaseId = `plan-step-${index + 1}`;
		const start = getDateForWorkdayIndexCal(startDate, step.dayStart, companyCalendar);
		const end = getDateForWorkdayIndexCal(startDate, step.dayEnd, companyCalendar);
		const duration = Math.max(1, step.dayEnd - step.dayStart + 1);
		const task: GanttTask = {
			id: phaseId,
			name: step.name,
			startDate: start.toISOString(),
			endDate: end.toISOString(),
			duration,
			progress: 0,
			dependencies: index > 0 ? [`plan-step-${index}`] : [],
			type: 'phase',
			status: 'not_started',
			description: step.description.length > 200 ? step.description.slice(0, 200) + '...' : step.description
		};
		tasks.push(task);
	});

	return tasks;
}

const DEFAULT_OFFER_TIMELINE_ESTIMATE_EN = '4-6 weeks from approval to completion';
const DEFAULT_OFFER_TIMELINE_ESTIMATE_DE = '4-6 Wochen von Freigabe bis Fertigstellung';
const DEFAULT_OFFER_TIMELINE_ESTIMATE_HR = '4-6 tjedana od odobrenja do završetka';

/** Default offer timeline text when plan instructions cannot be summarized. */
export function defaultOfferTimelineEstimate(language: Language = 'en'): string {
	if (language === 'de') return DEFAULT_OFFER_TIMELINE_ESTIMATE_DE;
	if (language === 'hr') return DEFAULT_OFFER_TIMELINE_ESTIMATE_HR;
	return DEFAULT_OFFER_TIMELINE_ESTIMATE_EN;
}

/** Whether the value is still an auto-filled default (any locale), not user-edited text. */
export function isDefaultOfferTimelineEstimate(value: string): boolean {
	const trimmed = value.trim();
	if (!trimmed) return false;
	if (
		trimmed === DEFAULT_OFFER_TIMELINE_ESTIMATE_EN ||
		trimmed === DEFAULT_OFFER_TIMELINE_ESTIMATE_DE ||
		trimmed === DEFAULT_OFFER_TIMELINE_ESTIMATE_HR
	) {
		return true;
	}
	return isAutoTimelineSummaryFromInstructions(trimmed);
}

function isAutoTimelineSummaryFromInstructions(value: string): boolean {
	return (
		/^(Around|Approx\.?) \d+ weeks? from start$/i.test(value) ||
		/^(Rund|Ca\.?) \d+ Wochen? ab Start$/i.test(value) ||
		/^(Oko|Otprilike) \d+ tjedan(a)? od početka$/i.test(value)
	);
}

/** Human-readable timeline summary from plan instructions (e.g. "Around 4 weeks from start"). */
export function timelineSummaryTextFromPlanInstructions(
	instructions: string[],
	language: Language = 'en'
): string {
	const steps = parseImplementationInstructions(instructions);
	if (steps.length === 0) return '';
	const last = steps[steps.length - 1];
	const totalDays = last.dayEnd;
	const weeks = totalDays <= 7 ? 1 : Math.ceil(totalDays / 7);
	if (language === 'de') {
		if (weeks <= 1) return 'Rund 1 Woche ab Start';
		return `Rund ${weeks} Wochen ab Start`;
	}
	if (language === 'hr') {
		if (weeks <= 1) return 'Oko 1 tjedan od početka';
		return `Oko ${weeks} tjedana od početka`;
	}
	if (weeks <= 1) return 'Around 1 week from start';
	return `Around ${weeks} weeks from start`;
}

/** Build a TimelineSummary from existing Gantt tasks (e.g. plan-based or synthetic). Used for preview and seeker view. */
export function timelineSummaryFromGanttTasks(
	tasks: GanttTask[],
	gardenerCountOverride?: number
): TimelineSummary {
	if (tasks.length === 0) {
		return {
			startDate: new Date().toISOString(),
			endDate: new Date().toISOString(),
			totalDuration: 0,
			gardenerCount: gardenerCountOverride ?? 0,
			milestones: [],
			phases: []
		};
	}
	const start = new Date(tasks[0].startDate);
	const end = tasks.reduce((latest, t) => {
		const taskEnd = new Date(t.endDate);
		return taskEnd > latest ? taskEnd : latest;
	}, new Date(tasks[0].endDate));
	// Use working days so duration matches the Gantt (tasks are in workdays; weekends are non-working)
	const totalDuration = countWorkdaysBetween(start, end);
	// Count unique gardeners from both legacy single gardenerId and newer gardenerIds array
	const gardenerIdSet = new Set<string>();
	tasks.forEach(t => {
		if (t.gardenerId) {
			gardenerIdSet.add(String(t.gardenerId));
		}
		if (Array.isArray(t.gardenerIds)) {
			t.gardenerIds
				.filter(Boolean)
				.forEach(id => gardenerIdSet.add(String(id)));
		}
	});
	const gardenerCount = gardenerCountOverride ?? gardenerIdSet.size;
	const milestones: TimelineMilestone[] = tasks
		.filter(t => t.type === 'phase' || t.type === 'milestone')
		.map(t => ({ name: t.name, date: t.startDate, status: 'planned' as const }));
	milestones.push({ name: 'Project Completion', date: end.toISOString(), status: 'planned' });
	const phases: TimelinePhase[] = tasks
		.filter(t => t.type === 'phase' || t.type === 'task')
		.map(t => ({
			name: t.name,
			startDate: t.startDate,
			endDate: t.endDate,
			duration: t.duration ?? 0,
			status: t.status
		}));
	return {
		startDate: start.toISOString(),
		endDate: end.toISOString(),
		totalDuration,
		gardenerCount,
		milestones,
		phases
	};
}

/**
 * Apply a task update and shift all dependent tasks by the same workday delta.
 * When a gardener extends (or shortens) a task in the offer Gantt, subsequent tasks move accordingly.
 * Uses workdays so the timeline stays consistent with the rest of the Gantt.
 */
export function applyTaskUpdateAndShiftDependents(
	tasks: GanttTask[],
	taskId: string,
	updates: Partial<GanttTask>,
	projectStartDate: Date,
	companyCalendar?: CompanyCalendar | null
): GanttTask[] {
	const taskMap = new Map(tasks.map(t => [t.id, t]));
	const updated = tasks.map(t => (t.id === taskId ? { ...t, ...updates } : { ...t }));

	const edited = updated.find(t => t.id === taskId);
	if (!edited) return updated;

	// Resolve final dates for the edited task (in case updates only had duration or partial dates)
	const oldTask = taskMap.get(taskId)!;
	let newStart = updates.startDate ? new Date(updates.startDate) : new Date(edited.startDate);
	let newEnd = updates.endDate ? new Date(updates.endDate) : new Date(edited.endDate);
	if (updates.duration != null && !updates.endDate) {
		newEnd = endDateForWorkingDays(newStart, updates.duration, companyCalendar);
	}
	const oldEnd = new Date(oldTask.endDate);
	const shiftWorkdays =
		getWorkdayIndexCal(projectStartDate, newEnd, companyCalendar) -
		getWorkdayIndexCal(projectStartDate, oldEnd, companyCalendar);
	if (shiftWorkdays === 0) {
		// Update only the edited task (dates might have been normalized)
		return updated.map(t =>
			t.id === taskId
				? {
						...t,
						startDate: newStart.toISOString(),
						endDate: newEnd.toISOString(),
						duration: updates.duration ?? edited.duration ?? oldTask.duration
					}
				: t
		);
	}

	const firstTaskWorkday = firstWorkday(new Date(projectStartDate), companyCalendar);

	// Collect all task IDs that depend on the edited task (directly or transitively)
	const dependents = new Set<string>();
	function collectDependents(id: string) {
		updated.forEach(t => {
			if (t.dependencies?.includes(id) && !dependents.has(t.id)) {
				dependents.add(t.id);
				collectDependents(t.id);
			}
		});
	}
	collectDependents(taskId);

	// Apply shift to edited task's dates (normalize) and to all dependents
	return updated.map(t => {
		if (t.id === taskId) {
			return {
				...t,
				startDate: newStart.toISOString(),
				endDate: newEnd.toISOString(),
				duration: updates.duration ?? edited.duration ?? oldTask.duration
			};
		}
		if (!dependents.has(t.id)) return t;
		const oldStart = new Date(t.startDate);
		const oldEndT = new Date(t.endDate);
		const idxStart = getWorkdayIndexCal(projectStartDate, oldStart, companyCalendar);
		const idxEnd = getWorkdayIndexCal(projectStartDate, oldEndT, companyCalendar);
		const newStartT = getDateForWorkdayIndexCal(firstTaskWorkday, Math.max(1, idxStart + shiftWorkdays), companyCalendar);
		const newEndT = getDateForWorkdayIndexCal(firstTaskWorkday, Math.max(1, idxEnd + shiftWorkdays), companyCalendar);
		const duration = t.duration ?? countWorkdaysBetween(oldStart, oldEndT);
		return {
			...t,
			startDate: newStartT.toISOString(),
			endDate: newEndT.toISOString(),
			duration
		};
	});
}

// Generate realistic project phases based on garden project patterns
function generateProjectPhases(budget: number, startDate: Date, language: Language = 'en'): Array<{
	name: string;
	duration: number;
	dependencies: string[];
	skills: string[];
}> {
	const isLargeProject = budget > 2000;
	const isMediumProject = budget > 1000 && budget <= 2000;
	
	const t = (en: string, de: string, hr: string) => (language === 'de' ? de : language === 'hr' ? hr : en);
	const phases = [
		{
			name: t('Site Preparation', 'Baustellenvorbereitung', 'Priprema gradilišta'),
			duration: isLargeProject ? 3 : isMediumProject ? 2 : 1,
			dependencies: [] as string[],
			skills: ['hardscaping', 'project_management']
		},
		{
			name: t('Material Delivery', 'Materiallieferung', 'Dostava materijala'),
			duration: 1,
			dependencies: [t('Site Preparation', 'Baustellenvorbereitung', 'Priprema gradilišta')],
			skills: ['project_management']
		},
		{
			name: t('Hardscaping', 'Bauarbeiten', 'Građevinski radovi'),
			duration: isLargeProject ? 7 : isMediumProject ? 5 : 3,
			dependencies: [t('Site Preparation', 'Baustellenvorbereitung', 'Priprema gradilišta')],
			skills: ['hardscaping']
		},
		{
			name: t('Planting - Trees', 'Pflanzung - Bäume', 'Sadnja - Drveće'),
			duration: isLargeProject ? 3 : isMediumProject ? 2 : 1,
			dependencies: [
				t('Material Delivery', 'Materiallieferung', 'Dostava materijala'),
				t('Hardscaping', 'Bauarbeiten', 'Građevinski radovi')
			],
			skills: ['planting']
		},
		{
			name: t('Planting - Shrubs', 'Pflanzung - Sträucher', 'Sadnja - Grmlje'),
			duration: isLargeProject ? 4 : isMediumProject ? 3 : 2,
			dependencies: [t('Planting - Trees', 'Pflanzung - Bäume', 'Sadnja - Drveće')],
			skills: ['planting']
		},
		{
			name: t('Planting - Ground Cover', 'Pflanzung - Bodendecker', 'Sadnja - Pokrivači tla'),
			duration: isLargeProject ? 3 : isMediumProject ? 2 : 1,
			dependencies: [t('Planting - Shrubs', 'Pflanzung - Sträucher', 'Sadnja - Grmlje')],
			skills: ['planting']
		},
		{
			name: t('Irrigation Setup', 'Installation der Bewässerung', 'Postavljanje navodnjavanja'),
			duration: isLargeProject ? 4 : isMediumProject ? 3 : 2,
			dependencies: [t('Planting - Ground Cover', 'Pflanzung - Bodendecker', 'Sadnja - Pokrivači tla')],
			skills: ['irrigation']
		},
		{
			name: t('Final Touches', 'Abschlussarbeiten', 'Završni radovi'),
			duration: isLargeProject ? 2 : 1,
			dependencies: [t('Irrigation Setup', 'Installation der Bewässerung', 'Postavljanje navodnjavanja')],
			skills: ['planting', 'hardscaping']
		}
	];
	
	return phases;
}

// Calculate critical path (tasks that if delayed, delay the entire project)
function calculateCriticalPath(tasks: GanttTask[]): Set<string> {
	const criticalTasks = new Set<string>();
	const taskMap = new Map(tasks.map(t => [t.id, t]));
	
	// Find tasks with no dependencies or dependencies that are all critical
	function isTaskCritical(taskId: string, visited = new Set<string>()): boolean {
		if (visited.has(taskId)) return false; // circular dependency
		visited.add(taskId);
		
		const task = taskMap.get(taskId);
		if (!task) return false;
		
		if (!task.dependencies || task.dependencies.length === 0) {
			return true; // start tasks are critical
		}
		
		// If all dependencies are critical, this task is critical
		return task.dependencies.every(depId => isTaskCritical(depId, new Set(visited)));
	}
	
	tasks.forEach(task => {
		if (isTaskCritical(task.id)) {
			criticalTasks.add(task.id);
		}
	});
	
	return criticalTasks;
}

/** Gardener-like shape for assignment (API response or mock). */
export type GardenerLike = { id: number | string; name: string; skills?: string[] };

// Assign gardeners to tasks based on skills (mock list when no selected gardeners)
function assignGardenersToTasks(
	tasks: GanttTask[],
	availableGardeners: Gardener[],
	organizationId?: string
): GanttTask[] {
	const filteredGardeners = organizationId
		? availableGardeners.filter(g => g.organizationId === organizationId)
		: availableGardeners;
	
	if (filteredGardeners.length === 0) {
		return tasks.map(t => ({
			...t,
			gardenerId: gardeners[Math.floor(Math.random() * gardeners.length)].id,
			gardenerName: gardeners[Math.floor(Math.random() * gardeners.length)].name
		}));
	}
	
	return tasks.map(task => {
		const taskWithSkills = task as GanttTask & { skills?: string[] };
		const taskSkills = taskWithSkills.skills || [];
		const suitableGardeners = filteredGardeners.filter(g => {
			if (taskSkills.length === 0) return true;
			return taskSkills.some((skill: string) => g.skills?.includes(skill));
		});
		const gardener = suitableGardeners.length > 0
			? suitableGardeners[Math.floor(Math.random() * suitableGardeners.length)]
			: filteredGardeners[Math.floor(Math.random() * filteredGardeners.length)];
		return {
			...task,
			gardenerId: gardener.id,
			gardenerName: gardener.name,
			gardenerIds: [String(gardener.id)],
			gardenerNames: [gardener.name]
		};
	});
}

/**
 * Assign selected gardeners to tasks by skills (one primary assignee per task). Use in offer wizard when team is chosen.
 */
export function assignSelectedGardenersToTasks(
	tasks: GanttTask[],
	selectedGardeners: GardenerLike[]
): GanttTask[] {
	if (selectedGardeners.length === 0) return tasks;
	
	return tasks.map(task => {
		const taskWithSkills = task as GanttTask & { skills?: string[] };
		const taskSkills = taskWithSkills.skills || [];
		const suitable = selectedGardeners.filter(g => {
			if (taskSkills.length === 0) return true;
			return taskSkills.some((s: string) => g.skills?.includes(s));
		});
		const gardener = suitable.length > 0
			? suitable[Math.floor(Math.random() * suitable.length)]
			: selectedGardeners[Math.floor(Math.random() * selectedGardeners.length)];
		const idStr = String(gardener.id);
		return {
			...task,
			gardenerId: idStr,
			gardenerName: gardener.name,
			gardenerIds: [idStr],
			gardenerNames: [gardener.name]
		};
	});
}

/** PTO entry shape (from API). */
export type PtoEntry = { start_date: string; end_date: string; label?: string | null };

/**
 * Extend task end dates to skip assignee PTO (and set hasPtoInRange). Uses company calendar for working days.
 */
export function adjustTaskDatesForPTO(
	tasks: GanttTask[],
	projectStartDate: Date,
	companyCalendar: CompanyCalendar | null | undefined,
	ptoByUserId: Record<string, PtoEntry[]>
): GanttTask[] {
	const cal = normalizeCompanyCalendar(companyCalendar);
	const { workingDays, nonWorkingDates } = cal;

	return tasks.map(task => {
		const assigneeIds = task.gardenerIds && task.gardenerIds.length > 0 ? task.gardenerIds : (task.gardenerId ? [task.gardenerId] : []);
		if (assigneeIds.length === 0) return task;

		const start = new Date(parseTimelineDate(task.startDate));
		let end = new Date(parseTimelineDate(task.endDate));
		let ptoDaysInRange = 0;
		const cur = new Date(start);
		cur.setHours(0, 0, 0, 0);
		const endD = new Date(end);
		endD.setHours(0, 0, 0, 0);
		
		while (cur <= endD) {
			if (!isCompanyWorkingDay(cur, workingDays, nonWorkingDates)) {
				cur.setDate(cur.getDate() + 1);
				continue;
			}
			const dateStr = toLocalDateString(cur);
			const onPto = assigneeIds.some(uid => {
				const entries = ptoByUserId[uid] || [];
				return isUserOnTimeOff(dateStr, entries);
			});
			if (onPto) ptoDaysInRange++;
			cur.setDate(cur.getDate() + 1);
		}
		
		if (ptoDaysInRange === 0) return task;
		
		const newEnd = addCompanyWorkdays(end, ptoDaysInRange, workingDays, nonWorkingDates);
		return {
			...task,
			endDate: toTimelineDateIso(newEnd),
			hasPtoInRange: true
		};
	});
}

/** Parse timeline ISO strings at local noon (matches toTimelineDateIso). */
function parseTimelineDate(value: string): Date {
	const dateOnly = value.split('T')[0];
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
		return new Date(`${dateOnly}T12:00:00`);
	}
	return new Date(value);
}

export function generateSyntheticGanttData(
	projectType: string,
	budget: number,
	startDate: Date = new Date(),
	organizationId?: string,
	companyCalendar?: CompanyCalendar | null,
	language: Language = 'en'
): GanttTask[] {
	const phases = generateProjectPhases(budget, startDate, language);
	const tasks: GanttTask[] = [];
	let currentDate = firstWorkday(new Date(startDate), companyCalendar);
	const phaseMap = new Map<string, GanttTask>();
	
	phases.forEach((phase, index) => {
		const phaseId = `phase-${index + 1}`;
		let phaseStartDate = new Date(currentDate);
		
		if (phase.dependencies.length > 0) {
			const depEndDates = phase.dependencies.map(depName => {
				const depPhase = phases.find(p => p.name === depName);
				if (!depPhase) return phaseStartDate;
				const depTask = Array.from(phaseMap.values()).find(t => t.name === depName);
				if (depTask) return new Date(depTask.endDate);
				return phaseStartDate;
			});
			const latestDepDate = new Date(Math.max(...depEndDates.map(d => d.getTime())));
			phaseStartDate = firstWorkday(latestDepDate, companyCalendar);
		}
		
		const phaseEndDate = addWorkdaysCal(phaseStartDate, phase.duration, companyCalendar);
		
		const task: GanttTask & { skills?: string[] } = {
			id: phaseId,
			name: phase.name,
			startDate: phaseStartDate.toISOString(),
			endDate: phaseEndDate.toISOString(),
			duration: phase.duration,
			progress: 0,
			dependencies: phase.dependencies.map(depName => {
				const depPhase = phases.find(p => p.name === depName);
				if (!depPhase) return '';
				const depIndex = phases.indexOf(depPhase);
				return `phase-${depIndex + 1}`;
			}).filter(id => id !== ''),
			type: 'phase',
			status: 'not_started',
			skills: phase.skills
		};
		
		phaseMap.set(phase.name, task);
		tasks.push(task);
		
		currentDate = new Date(phaseEndDate);
	});
	
	const tasksWithGardeners = assignGardenersToTasks(tasks, gardeners, organizationId);
	const criticalPath = calculateCriticalPath(tasksWithGardeners);
	tasksWithGardeners.forEach(task => {
		task.isCritical = criticalPath.has(task.id);
	});
	
	return tasksWithGardeners;
}

export function generateTimelineSummary(
	projectType: string,
	budget: number,
	startDate: Date = new Date(),
	organizationId?: string,
	language: Language = 'en'
): TimelineSummary {
	const ganttData = generateSyntheticGanttData(projectType, budget, startDate, organizationId, undefined, language);
	
	if (ganttData.length === 0) {
		return {
			startDate: startDate.toISOString(),
			endDate: startDate.toISOString(),
			totalDuration: 0,
			gardenerCount: 0,
			milestones: [],
			phases: []
		};
	}
	
	const start = new Date(ganttData[0].startDate);
	const end = new Date(ganttData[ganttData.length - 1].endDate);
	const totalDuration = countWorkdaysBetween(start, end);
	
	// Get unique gardeners
	const gardenerIds = new Set(ganttData.map(t => t.gardenerId).filter(Boolean));
	const gardenerCount = gardenerIds.size;
	
	// Create milestones at phase boundaries
	const milestones: TimelineMilestone[] = ganttData
		.filter(t => t.type === 'phase' || t.type === 'milestone')
		.map(t => ({
			name: t.name,
			date: t.startDate,
			status: 'planned' as const
		}));
	
	// Add completion milestone
	milestones.push({
		name: language === 'de' ? 'Projektabschluss' : language === 'hr' ? 'Završetak projekta' : 'Project Completion',
		date: end.toISOString(),
		status: 'planned'
	});
	
	// Create phases summary
	const phases: TimelinePhase[] = ganttData
		.filter(t => t.type === 'phase')
		.map(t => ({
			name: t.name,
			startDate: t.startDate,
			endDate: t.endDate,
			duration: t.duration,
			status: t.status
		}));
	
	return {
		startDate: start.toISOString(),
		endDate: end.toISOString(),
		totalDuration,
		gardenerCount,
		milestones,
		phases
	};
}

/** Working days between two dates (company calendar when provided). */
export function countWorkdaysBetweenCal(
	start: Date,
	end: Date,
	companyCalendar?: CompanyCalendar | null
): number {
	const { workingDays, nonWorkingDates } = resolveScheduleCalendar(
		companyCalendar?.workingDays,
		companyCalendar?.nonWorkingDates
	);
	return countCompanyWorkdaysBetween(start, end, workingDays, nonWorkingDates);
}

/** First company/working day strictly after the given date (next calendar day onward). */
export function nextWorkdayAfter(from: Date, companyCalendar?: CompanyCalendar | null): Date {
	const probe = new Date(from);
	probe.setDate(probe.getDate() + 1);
	probe.setHours(12, 0, 0, 0);
	return firstWorkday(probe, companyCalendar);
}

/** Resolve task working-day length from duration field or from start/end. */
export function getTaskWorkingDuration(task: GanttTask, companyCalendar?: CompanyCalendar | null): number {
	if (typeof task.duration === 'number' && task.duration > 0) return Math.floor(task.duration);
	const start = new Date(task.startDate);
	const end = new Date(task.endDate);
	const counted = countWorkdaysBetweenCal(start, end, companyCalendar);
	return Math.max(1, counted);
}

/** End date for a task that spans `workingDays` working days starting on `start` (inclusive). */
export function endDateForWorkingDays(
	start: Date,
	workingDays: number,
	companyCalendar?: CompanyCalendar | null
): Date {
	const days = Math.max(1, workingDays);
	const anchor = firstWorkday(start, companyCalendar);
	const { workingDays: wd, nonWorkingDates } = resolveScheduleCalendar(
		companyCalendar?.workingDays,
		companyCalendar?.nonWorkingDates
	);
	return addCompanyWorkdays(anchor, days, wd, nonWorkingDates);
}

/**
 * Lay out tasks in array order: chained start dates, linear dependencies, preserved working-day durations.
 */
export function scheduleTimelineTasksSequentially(
	tasksInOrder: GanttTask[],
	projectStartDate: Date,
	companyCalendar?: CompanyCalendar | null,
	options?: { clearAssignments?: boolean }
): GanttTask[] {
	if (tasksInOrder.length === 0) return [];

	const cal = normalizeCompanyCalendar(companyCalendar);
	// Preserve list order (execution order from UI); do not re-sort by stale start dates.
	const ordered = [...tasksInOrder];

	const anchor = firstWorkday(new Date(projectStartDate), cal);
	let previousEnd: Date | null = null;
	const scheduled: GanttTask[] = [];

	ordered.forEach((task, index) => {
		const workingDays = getTaskWorkingDuration(task, cal);
		const start =
			index === 0 || !previousEnd
				? new Date(anchor)
				: nextWorkdayAfter(previousEnd, cal);
		const end = endDateForWorkingDays(start, workingDays, cal);
		const prevId = index > 0 ? scheduled[index - 1].id : undefined;

		const cleared = options?.clearAssignments
			? {
					gardenerId: undefined,
					gardenerName: undefined,
					gardenerIds: undefined,
					gardenerNames: undefined,
					hasPtoInRange: undefined
				}
			: {};

		scheduled.push({
			...task,
			...cleared,
			startDate: toTimelineDateIso(start),
			endDate: toTimelineDateIso(end),
			duration: workingDays,
			dependencies: prevId ? [prevId] : []
		});
		previousEnd = end;
	});

	return scheduled;
}

/** Swap task order by index and reschedule from project start. */
export function moveTimelineTaskInOrder(
	tasks: GanttTask[],
	index: number,
	direction: 'up' | 'down',
	projectStartDate: Date,
	companyCalendar?: CompanyCalendar | null
): GanttTask[] {
	const target = direction === 'up' ? index - 1 : index + 1;
	if (target < 0 || target >= tasks.length) return tasks;
	const next = [...tasks];
	[next[index], next[target]] = [next[target], next[index]];
	return scheduleTimelineTasksSequentially(next, projectStartDate, companyCalendar, { clearAssignments: true });
}

/** Remove a leading "Day N:" / "Days N-M:" label (EN/DE/HR) from instruction body text. */
export function stripLeadingInstructionDayLabel(text: string): string {
	const trimmed = text.trim();
	if (!trimmed) return trimmed;
	const rangePattern = /^(?:Days|Tage|Dani)\s*\d+\s*[-–]\s*\d+\s*:\s*/iu;
	const singlePattern = /^(?:Day|Tag|Dan)\s*\d+\s*:\s*/iu;
	if (rangePattern.test(trimmed)) return trimmed.replace(rangePattern, '').trim();
	if (singlePattern.test(trimmed)) return trimmed.replace(singlePattern, '').trim();
	return trimmed;
}

export function formatInstructionDayLine(
	dayStart: number,
	dayEnd: number,
	description: string,
	language: Language = 'en'
): string {
	const text = stripLeadingInstructionDayLabel(description);
	if (dayStart === dayEnd) {
		if (language === 'de') return `Tag ${dayStart}: ${text}`;
		if (language === 'hr') return `Dan ${dayStart}: ${text}`;
		return `Day ${dayStart}: ${text}`;
	}
	if (language === 'de') return `Tage ${dayStart}-${dayEnd}: ${text}`;
	if (language === 'hr') return `Dani ${dayStart}-${dayEnd}: ${text}`;
	return `Days ${dayStart}-${dayEnd}: ${text}`;
}

/** Build cumulative day ranges and sync instructions / timeline_tasks / metadata order. */
export function syncAiContentFromTimeline(
	content: {
		instructions?: string[];
		timeline_tasks?: AiTimelineTask[];
		instruction_metadata?: Array<{
			required_skills_for_step?: string[];
			required_gardeners_for_step?: number;
		}>;
	},
	tasks: GanttTask[],
	language: Language = 'en'
): {
	instructions: string[];
	timeline_tasks: AiTimelineTask[];
	instruction_metadata?: Array<{
		required_skills_for_step: string[];
		required_gardeners_for_step: number;
	}>;
} {
	const metaByOldIndex = new Map<number, { required_skills_for_step: string[]; required_gardeners_for_step: number }>();
	(content.instruction_metadata ?? []).forEach((m, i) => {
		metaByOldIndex.set(i, {
			required_skills_for_step: m.required_skills_for_step ?? [],
			required_gardeners_for_step: m.required_gardeners_for_step ?? 1
		});
	});

	const parseStepIndex = (id: string): number | null => {
		const m = /^plan-step-(\d+)$/.exec(id);
		return m ? parseInt(m[1], 10) - 1 : null;
	};

	let dayCursor = 1;
	const instructions: string[] = [];
	const timeline_tasks: AiTimelineTask[] = [];
	const instruction_metadata: Array<{
		required_skills_for_step: string[];
		required_gardeners_for_step: number;
	}> = [];

	tasks.forEach((task, orderIndex) => {
		const workingDays = Math.max(1, task.duration ?? 1);
		const dayStart = dayCursor;
		const dayEnd = dayCursor + workingDays - 1;
		dayCursor = dayEnd + 1;

		const description = (task.description || task.name || '').trim() || task.name;
		instructions.push(formatInstructionDayLine(dayStart, dayEnd, description, language));

		const oldIdx = parseStepIndex(task.id) ?? orderIndex;
		const prevMeta = metaByOldIndex.get(oldIdx);

		timeline_tasks.push({
			title: task.name,
			description,
			dayStart,
			dayEnd,
			dependsOnSteps: orderIndex > 0 ? [orderIndex] : undefined,
			seasonal_ban_candidate: task.legalSeasonalBanCandidate,
			seasonal_ban_reason: task.legalSeasonalBanReason,
			required_skills_for_step: task.requiredSkills ?? prevMeta?.required_skills_for_step,
			required_gardeners_for_step: task.requiredGardeners ?? prevMeta?.required_gardeners_for_step
		});

		instruction_metadata.push({
			required_skills_for_step: task.requiredSkills ?? prevMeta?.required_skills_for_step ?? [],
			required_gardeners_for_step:
				typeof task.requiredGardeners === 'number' && task.requiredGardeners > 0
					? task.requiredGardeners
					: prevMeta?.required_gardeners_for_step ?? 1
		});
	});

	return {
		instructions,
		timeline_tasks,
		instruction_metadata: content.instruction_metadata ? instruction_metadata : undefined
	};
}

/**
 * Apply task edits using working days; cascade dependents when end moves.
 */
export function applyWorkdayTaskUpdate(
	tasks: GanttTask[],
	taskId: string,
	updates: Partial<GanttTask> & { startDate?: string; endDate?: string; duration?: number },
	projectStartDate: Date,
	companyCalendar?: CompanyCalendar | null
): GanttTask[] {
	const taskMap = new Map(tasks.map((t) => [t.id, t]));
	const current = taskMap.get(taskId);
	if (!current) return tasks;

	let start = updates.startDate ? new Date(updates.startDate) : new Date(current.startDate);
	start = firstWorkday(start, companyCalendar);

	let workingDays: number;
	if (typeof updates.duration === 'number' && updates.duration > 0) {
		workingDays = Math.floor(updates.duration);
	} else if (updates.endDate) {
		const endProbe = firstWorkday(new Date(updates.endDate), companyCalendar);
		workingDays = countWorkdaysBetweenCal(start, endProbe, companyCalendar);
	} else {
		workingDays = getTaskWorkingDuration(current, companyCalendar);
	}
	workingDays = Math.max(1, workingDays);

	const next = tasks.map((t) => {
		if (t.id !== taskId) return t;
		return {
			...t,
			duration: workingDays,
			...(updates.startDate ? { startDate: toTimelineDateIso(start) } : {})
		};
	});

	return scheduleTimelineTasksSequentially(next, projectStartDate, companyCalendar);
}

export type TimelineGardenerCandidate = {
	id: number;
	name: string;
	skills: string[];
	available: boolean;
};

export type TimelineAssignConflictChecker = (
	startDate: Date,
	endDate: Date,
	gardenerIds: string[]
) => boolean;

export type AutoAssignGardenersOptions = {
	/** When true, tasks that already have gardenerIds keep their assignees (dates may still shift for sequence/conflicts). */
	onlyUnassigned?: boolean;
};

/** Heuristic headcount when AI omits required_gardeners_for_step (e.g. older plans). */
export function inferRequiredGardenersForTask(
	title: string,
	description: string,
	recommendedTeamSize: number
): number {
	const team = Math.max(1, Math.min(10, Math.floor(recommendedTeamSize) || 1));
	const text = `${title} ${description}`.toLowerCase();

	if (
		/\b(inspect|inspection|final check|touch[- ]?up|cleanup|clean[- ]?up|furnish|furniture|decor|dressing)\b/.test(
			text
		) ||
		/\b(light plant|mulch|watering|seeding|turf lay)\b/.test(text)
	) {
		return 1;
	}

	if (
		/\b(excavat|demolit|concrete|foundation|retaining wall|structural|pool install|pond install)\b/.test(text) ||
		/\b(build wall|masonry|hardscape|paving|paver|deck build|pergola|fence install|shed build)\b/.test(text) ||
		/\b(water feature|fountain install|utility trench|rough[- ]?in|compaction|sub[- ]?base)\b/.test(text) ||
		/\b(gravel base|site clear|leveling|heavy lift|crane)\b/.test(text)
	) {
		return team >= 2 ? team : 2;
	}

	return 1;
}

export function resolveTaskRequiredGardeners(
	task: GanttTask,
	recommendedTeamSize: number
): number {
	if (typeof task.requiredGardeners === 'number' && task.requiredGardeners > 0) {
		return Math.min(10, Math.floor(task.requiredGardeners));
	}
	return inferRequiredGardenersForTask(task.name, task.description ?? '', recommendedTeamSize);
}

/** Whether a timeline task has at least one gardener assigned. */
export function taskHasAssignedGardeners(task: GanttTask): boolean {
	if (Array.isArray(task.gardenerIds) && task.gardenerIds.length > 0) return true;
	return !!task.gardenerId;
}

export function getTaskAssigneeIds(task: GanttTask): string[] {
	if (Array.isArray(task.gardenerIds) && task.gardenerIds.length > 0) {
		return task.gardenerIds.map(String);
	}
	if (task.gardenerId) return [String(task.gardenerId)];
	return [];
}

/**
 * Assign gardeners per task (skills + load balance), sliding task dates forward on conflicts.
 * Tasks are processed in start-date order; following tasks are rescheduled after each move.
 */
export function autoAssignGardenersToTimelineTasks(
	tasks: GanttTask[],
	candidates: TimelineGardenerCandidate[],
	planRequiredSkills: string[],
	projectStartDate: Date,
	companyCalendar: CompanyCalendar | null | undefined,
	hasConflict: TimelineAssignConflictChecker,
	defaultTeamSize = 1,
	options?: AutoAssignGardenersOptions
): GanttTask[] {
	if (tasks.length === 0 || candidates.length === 0) return tasks;

	const ordered = [...tasks].sort(
		(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
	);
	const assignmentCounts = new Map<number, number>();

	function scoreGardener(required: string[], gardenerId: number, gardenerSkills: string[]): number {
		let base = 1;
		if (required.length > 0) {
			const req = Array.from(new Set(required));
			const have = Array.from(new Set((gardenerSkills || []).map(String)));
			const inter = req.filter((s) => have.includes(s));
			base = inter.length / req.length;
		}
		const penalty = (assignmentCounts.get(gardenerId) ?? 0) * 0.1;
		return base - penalty;
	}

	function pickGardeners(requiredSkills: string[], requiredGardeners: number): TimelineGardenerCandidate[] {
		const poolAvailable = candidates.filter((c) => c.available);
		const pool = poolAvailable.length > 0 ? poolAvailable : candidates;
		const scored = pool
			.map((c) => ({ ...c, score: scoreGardener(requiredSkills, c.id, c.skills) }))
			.sort((a, b) => (a.score === b.score ? a.id - b.id : b.score - a.score));
		const strictMatch = scored
			.slice(0, requiredGardeners)
			.filter((c) => c.score > 0 || requiredSkills.length === 0);
		// Skills are a preference: still assign the best-available team when no one fully matches.
		if (strictMatch.length > 0) return strictMatch;
		return scored.slice(0, Math.max(1, requiredGardeners));
	}

	const scheduled = scheduleTimelineTasksSequentially(ordered, projectStartDate, companyCalendar);
	const result: GanttTask[] = [];

	for (let i = 0; i < scheduled.length; i++) {
		const task = scheduled[i];
		const requiredSkills =
			task.requiredSkills && task.requiredSkills.length > 0 ? task.requiredSkills : planRequiredSkills;
		const requiredGardeners =
			typeof task.requiredGardeners === 'number' && task.requiredGardeners > 0
				? task.requiredGardeners
				: defaultTeamSize;
		const workingDays = getTaskWorkingDuration(task, companyCalendar);

		let start = new Date(task.startDate);
		if (i > 0 && result.length > 0) {
			const prevEnd = new Date(result[result.length - 1].endDate);
			const minStart = nextWorkdayAfter(prevEnd, companyCalendar);
			if (start < minStart) start = minStart;
		}

		const existingIds = getTaskAssigneeIds(task);
		if (options?.onlyUnassigned && existingIds.length > 0) {
			const existingNames =
				Array.isArray(task.gardenerNames) && task.gardenerNames.length > 0
					? task.gardenerNames.map(String)
					: existingIds.map((id) => {
							const match = candidates.find((c) => String(c.id) === id);
							return match?.name ?? id;
						});
			let end = endDateForWorkingDays(start, workingDays, companyCalendar);
			let guard = 0;
			while (existingIds.length > 0 && hasConflict(start, end, existingIds) && guard < 370) {
				start = nextWorkdayAfter(start, companyCalendar);
				end = endDateForWorkingDays(start, workingDays, companyCalendar);
				guard++;
			}
			for (const id of existingIds) {
				const numId = Number(id);
				if (!Number.isNaN(numId)) {
					assignmentCounts.set(numId, (assignmentCounts.get(numId) ?? 0) + 1);
				}
			}
			result.push({
				...task,
				startDate: start.toISOString(),
				endDate: end.toISOString(),
				duration: workingDays,
				requiredSkills,
				requiredGardeners,
				gardenerIds: existingIds,
				gardenerNames: existingNames,
				gardenerId: existingIds[0],
				gardenerName: existingNames[0]
			});
			continue;
		}

		let chosen = pickGardeners(requiredSkills, requiredGardeners);
		let end = endDateForWorkingDays(start, workingDays, companyCalendar);
		let guard = 0;
		const gardenerIds = () => chosen.map((c) => String(c.id));

		while (chosen.length > 0 && hasConflict(start, end, gardenerIds()) && guard < 370) {
			start = nextWorkdayAfter(start, companyCalendar);
			end = endDateForWorkingDays(start, workingDays, companyCalendar);
			guard++;
		}

		for (const c of chosen) {
			assignmentCounts.set(c.id, (assignmentCounts.get(c.id) ?? 0) + 1);
		}

		result.push({
			...task,
			startDate: start.toISOString(),
			endDate: end.toISOString(),
			duration: workingDays,
			requiredSkills,
			requiredGardeners,
			gardenerIds: chosen.map((c) => String(c.id)),
			gardenerNames: chosen.map((c) => c.name),
			gardenerId: chosen[0] ? String(chosen[0].id) : undefined,
			gardenerName: chosen[0]?.name
		});
	}

	return scheduleTimelineTasksSequentially(result, projectStartDate, companyCalendar);
}

